import * as React from 'react';
import { BookUser, ChevronDown, User as UserIcon } from 'lucide-react';
import type {
  ClientLight,
  ClientListLightQuery,
  ClientListLightResponse,
} from '@proptr/shared';
import { EmptyState } from '../components/EmptyState.js';
import {
  HeaderLabel,
  TableCard,
  TableHeaderRow,
  TableLoading,
  TableMessage,
  TablePagination,
} from '../components/table/index.js';
import { AppShell } from '../components/layout/AppShell.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

type SortKey = NonNullable<ClientListLightQuery['sort']>;

export function ContactsPage() {
  const [sortBy, setSortBy] = React.useState<SortKey>('latestInquiry');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);

  const [data, setData] = React.useState<ClientListLightResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // True while a filter/sort/page change refetches with rows
  // already on screen — drives TableCard's dim + "Updating" pill.
  const [refreshing, setRefreshing] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setRefreshing(true);
    client.clients
      .listClientsLight({ sort: sortBy, order, page, limit: pageSize })
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      })
      .finally(() => {
        if (alive) setRefreshing(false);
      });
    return () => {
      alive = false;
    };
  }, [sortBy, order, page, pageSize]);

  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const totalPages = meta?.pages ?? 1;
  const items = data?.items ?? null;

  return (
    <AppShell>
      <div className="flex flex-1 flex-col space-y-6">
        <div>
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            Contacts
          </h1>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground">
            <span className="text-foreground">{total}</span> total
          </p>
        </div>

        <TableCard
          minWidth={1200}
          busy={refreshing && items !== null}
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          }
        >
          {/* Table header — column widths/flex must stay in sync with
              ContactRow's LabeledCell widths below. */}
          <TableHeaderRow>
                <span className="h-8 w-8 shrink-0" aria-hidden />
                <span className="h-10 w-10 shrink-0" aria-hidden />
                <HeaderLabel className="min-w-[180px] flex-[2_1_180px]">
                  Name
                </HeaderLabel>
                <HeaderLabel className="min-w-[160px] flex-[1.5_1_160px]">
                  Phone
                </HeaderLabel>
                <HeaderLabel className="min-w-[200px] flex-[2.5_1_200px]">
                  Email
                </HeaderLabel>
                <HeaderLabel className="min-w-[180px] flex-[2_1_180px]">
                  Latest inquiry
                </HeaderLabel>
                <HeaderLabel className="min-w-[120px] flex-[1_1_120px] text-right">
                  Total inquiries
                </HeaderLabel>
          </TableHeaderRow>
              {error ? (
                <TableMessage tone="destructive">{error}</TableMessage>
              ) : items === null ? (
                <TableLoading />
              ) : items.length === 0 ? (
                <EmptyState
                  icon={BookUser}
                  title="No contacts yet"
                  description="Contacts are created automatically from inquiries — when a lead reaches out through one of your proptrees, they show up here."
                />
              ) : (
                items.map((contact) => (
                  <ContactRow key={contact._id} contact={contact} />
                ))
              )}
        </TableCard>
      </div>
    </AppShell>
  );
}

function ContactRow({ contact }: { contact: ClientLight }) {
  const [open, setOpen] = React.useState(false);
  const name = contact.name || '—';
  const phone = contact.phone || '—';
  const email = contact.email || '—';
  const latest = formatDateTime(contact.latestInquiry);

  return (
    <div
      className={cn(
        'border-b border-[hsl(var(--hairline))] last:border-b-0',
        open && 'bg-muted/20',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/60"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground">
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform',
              open && 'rotate-180',
            )}
          />
        </span>
        <ContactAvatar name={name} image={contact.image} />
        <LabeledCell
          className="min-w-[180px] flex-[2_1_180px]"
          value={name}
          label="Name"
        />
        <LabeledCell
          className="min-w-[160px] flex-[1.5_1_160px]"
          value={phone}
          label="Phone"
          mono
        />
        <LabeledCell
          className="min-w-[200px] flex-[2.5_1_200px]"
          value={email}
          label="Email"
        />
        <LabeledCell
          className="min-w-[180px] flex-[2_1_180px]"
          value={latest}
          label="Latest inquiry"
          mono
        />
        <LabeledCell
          className="min-w-[120px] flex-[1_1_120px] text-right"
          value={String(contact.totalInquiries)}
          label="Total inquiries"
          mono
          alignRight
        />
      </button>
    </div>
  );
}

// Cell for a contact row. The `label` prop is accepted for backwards-compat
// with the old inline-label layout but no longer rendered — the table now
// has a proper top header row, so per-row labels would be redundant.
function LabeledCell({
  value,
  className,
  mono,
  alignRight,
}: {
  value: string;
  /** Unused — kept on the type so call sites compile unchanged. */
  label?: string;
  className?: string;
  mono?: boolean;
  alignRight?: boolean;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div
        className={cn(
          'truncate font-sans text-[14px] font-medium text-foreground',
          mono && 'font-mono tabular-nums',
          alignRight && 'text-right',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function ContactAvatar({
  name,
  image,
}: {
  name: string;
  image?: string | null;
}) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-10 w-10 shrink-0 rounded-full bg-muted object-cover"
      />
    );
  }
  if (name && name !== '—') {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-bold uppercase">
        {initials(name)}
      </div>
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
      <UserIcon className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toISOString().slice(0, 10);
  const time = d
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();
  return `${date} ${time}`;
}
